import React, { useEffect, useState } from "react";
import { User as UserModel } from "../../../models/user";
import * as UserApi from "../../../network/user_api";
import * as CollectiblesApi from "../../../network/collectibles_api";
import { useTranslation } from "react-i18next";
import Divider from "../../../components/Decors/Divider/Divider";
import { GiTwoCoins } from "react-icons/gi";
import Collectibles from "./Collectibles";
import { Spinner } from "react-bootstrap";
import { SplashScreen } from "../../../models/splashScreens";
import { ProfileImage } from "../../../models/profileImages";
import { CardTheme } from "../../../models/cardThemes";
import styles from "../StorePage.module.scss"; // Import styles for the menu

const Store = () => {
  const { t } = useTranslation();
  const [user, setUser] = useState<UserModel>();
  const [userLoading, setUserLoading] = useState(true);
  const [showUserLoadingError, setShowUserLoadingError] = useState(false);

  const [splashScreens, setSplashScreens] = useState<SplashScreen[]>([]);
  const [profileImages, setProfileImages] = useState<ProfileImage[]>([]);
  const [cardThemes, setCardThemes] = useState<CardTheme[]>([]);

  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState<"splashScreens" | "profileImages" | "cardThemes">("splashScreens");

  useEffect(() => {
    async function loadUser() {
      try {
        setShowUserLoadingError(false);
        setUserLoading(true);
        const user = await UserApi.fetchUser();
        setUser(user);
      } catch (error) {
        console.error(error);
        setShowUserLoadingError(true);
      } finally {
        setUserLoading(false);
      }
    }
    loadUser();
  }, []);

  useEffect(() => {
    async function loadCollectibles() {
      try {
        setLoading(true);

        const [
          allSplashScreens,
          ownedSplashScreens,
          allProfileImages,
          ownedProfileImages,
          allCardThemes,
          ownedCardThemes,
        ] = await Promise.all([
          CollectiblesApi.fetchAllSplashScreens(),
          CollectiblesApi.fetchSplashScreens(),
          CollectiblesApi.fetchAllProfileImages(),
          CollectiblesApi.fetchProfileImages(),
          CollectiblesApi.fetchAllCardThemes(),
          CollectiblesApi.fetchCardThemes(),
        ]);

        const availableSplashScreens = Object.values(allSplashScreens).filter(
          (splash) => !ownedSplashScreens.some((owned) => owned.fileName === splash.fileName)
        );
        const availableProfileImages = Object.values(allProfileImages).filter(
          (profile) => !ownedProfileImages.some((owned) => owned.fileName === profile.fileName)
        );
        const availableCardThemes = Object.values(allCardThemes).filter(
          (cardTheme) => !ownedCardThemes.some((owned) => owned.fileName === cardTheme.fileName)
        );

        setSplashScreens(availableSplashScreens);
        setProfileImages(availableProfileImages);
        setCardThemes(availableCardThemes);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    }
    loadCollectibles();
  }, []);

  return (
    <>
      <div className="title-block-container">
        <h1>{t("pages.store")}</h1>
        <Divider />
      </div>

      {userLoading && <Spinner className="custom-spinner" animation="border" />}
      {showUserLoadingError && <p>{t("error.refresh")}</p>}

      {!userLoading && user && (
        <>
          <h2 className="lightText">
            <GiTwoCoins /> {user.coin}
          </h2>

          {/* Block-like Filter Menu */}
          <div className={styles.blockMenuContainer}>
            <div
              className={`${styles.blockMenuItem} ${selectedType === "splashScreens" ? styles.active : ""}`}
              onClick={() => setSelectedType("splashScreens")}
            >
              {t("store.splashScreens")}
            </div>
            <div
              className={`${styles.blockMenuItem} ${selectedType === "profileImages" ? styles.active : ""}`}
              onClick={() => setSelectedType("profileImages")}
            >
              {t("store.profileImages")}
            </div>
            <div
              className={`${styles.blockMenuItem} ${selectedType === "cardThemes" ? styles.active : ""}`}
              onClick={() => setSelectedType("cardThemes")}
            >
              {t("store.cardThemes")}
            </div>
          </div>

          {loading && <Spinner className="custom-spinner" animation="border" />}

          {!loading && (
            <Collectibles
              user={user}
              onProfileSaved={setUser}
              collectibles={
                selectedType === "splashScreens"
                  ? splashScreens
                  : selectedType === "profileImages"
                  ? profileImages
                  : cardThemes
              }
              collectibleType={selectedType}
            />
          )}
        </>
      )}
    </>
  );
};

export default Store;
